/**
 * Shared UI component for UI Page Tabs Header.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

import { UiFilterBarComponent } from '../ui-filter-bar/ui-filter-bar.component';
import { UiFilterOption } from '../ui-filter-bar/ui-filter-bar.types';
import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';

/**
 * COMPONENT: Ui Page Tabs Header
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Component({
  selector: 'ui-page-tabs-header',
  standalone: true,
  imports: [UiFilterBarComponent, UiLabelTranslatePipe],
  templateUrl: './ui-page-tabs-header.component.html',
  styleUrl: './ui-page-tabs-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiPageTabsHeaderComponent {
  /** PARENT-CHILD CONTRACT: title documents the input state boundary used by this file. */
  readonly title = input('');
  /** PARENT-CHILD CONTRACT: description documents the input state boundary used by this file. */
  readonly description = input('');
  readonly tabs = input<UiFilterOption[]>([]);
  /** PARENT-CHILD CONTRACT: activeTab documents the input state boundary used by this file. */
  readonly activeTab = input('');
  readonly tabSelected = output<string>();

  /**
   * BUSINESS LOGIC: onTabSelected
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onTabSelected(value: string): void {
    this.tabSelected.emit(value);
  }
}
